import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import filingService from '../services/filingService';

export const FILING_QUEUE_KEY = ['attorney', 'filing-queue'];
export const ORG_MEMBERS_KEY = ['attorney', 'org-members'];

/**
 * Hook to fetch the filing queue
 */
export function useFilingQueue(options = {}) {
  return useQuery({
    queryKey: [...FILING_QUEUE_KEY, options],
    queryFn: () => filingService.getFilingQueue(options),
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true
  });
}

/**
 * Hook to fetch a single filing application
 */
export function useFilingApplication(applicationId) {
  return useQuery({
    queryKey: [...FILING_QUEUE_KEY, applicationId],
    queryFn: () => filingService.getFilingApplication(applicationId),
    enabled: !!applicationId,
    staleTime: 1000 * 60 * 2
  });
}

/**
 * Hook to mark a jurisdiction as filed
 */
export function useMarkAsFiled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, data }) => filingService.markAsFiled(applicationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILING_QUEUE_KEY });
    }
  });
}

/**
 * Hook to update filing status
 */
export function useUpdateFilingStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, jurisdiction, status, data }) =>
      filingService.updateFilingStatus(applicationId, jurisdiction, status, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILING_QUEUE_KEY });
    }
  });
}

/**
 * Hook to assign an application to self or another attorney
 */
export function useAssignApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, attorneyId }) =>
      filingService.assignApplication(applicationId, attorneyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILING_QUEUE_KEY });
    }
  });
}

/**
 * Hook to transfer an application to another organization
 */
export function useTransferApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, targetOrgId, reason }) =>
      filingService.transferApplication(applicationId, targetOrgId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILING_QUEUE_KEY });
    }
  });
}

/**
 * Hook to unassign attorney from an application
 */
export function useUnassignApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (applicationId) =>
      filingService.unassignApplication(applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILING_QUEUE_KEY });
    }
  });
}

/**
 * Hook to recall an application back to MarkoTM (removes transfer AND attorney)
 */
export function useRecallApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (applicationId) =>
      filingService.recallApplication(applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILING_QUEUE_KEY });
    }
  });
}

/**
 * Hook to fetch org members for assignment (admins only)
 */
export function useOrgMembers(options = {}) {
  return useQuery({
    queryKey: ORG_MEMBERS_KEY,
    queryFn: () => filingService.getOrgMembers(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options.enabled !== false
  });
}

/**
 * Hook to amend an application (admin/attorneyadmin only)
 */
export function useAmendApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, data }) =>
      filingService.amendApplication(applicationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILING_QUEUE_KEY });
    }
  });
}

/**
 * Hook to send amendment notification email (admin/attorneyadmin only)
 */
export function useSendAmendmentEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, data }) =>
      filingService.sendAmendmentEmail(applicationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILING_QUEUE_KEY });
    }
  });
}

export function useApproveAmendment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, amendmentId }) =>
      filingService.approveAmendment(applicationId, amendmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILING_QUEUE_KEY });
    }
  });
}

export default {
  useFilingQueue,
  useFilingApplication,
  useMarkAsFiled,
  useUpdateFilingStatus,
  useAssignApplication,
  useTransferApplication,
  useUnassignApplication,
  useRecallApplication,
  useOrgMembers,
  useAmendApplication,
  useSendAmendmentEmail,
  useApproveAmendment
};
